import { useState, useEffect } from 'react';
import { useNavigate }        from 'react-router-dom';
import { useSessionStore }    from '../store/sessionStore.js';
import { closeSession, getPaymentsBySession } from '../api/pos.api.js';
import { IcoCash, IcoCard, IcoTransfer, IcoMixed, IcoCheck } from '../components/common/Icons.jsx';
import styles from './CloseSessionScreen.module.css';

const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

export default function CloseSessionScreen() {
  const navigate     = useNavigate();
  const session      = useSessionStore((s) => s.session);
  const terminal     = useSessionStore((s) => s.terminal);
  const closeSess    = useSessionStore((s) => s.closeSession);

  const [closingCash, setClosingCash] = useState('');
  const [notes,       setNotes]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [breakdown,   setBreakdown]   = useState(null);

  useEffect(() => {
    if (!session?._id) return;
    getPaymentsBySession(session._id).then((res) => {
      const list = res?.data ?? res ?? [];
      const payments = Array.isArray(list) ? list.filter((p) => p.status !== 'refunded') : [];
      const bd = { cash: 0, card: 0, transfer: 0, mixed: 0 };
      for (const p of payments) {
        const effective = p.status === 'partially_refunded'
          ? (p.total ?? 0) - (p.refund_amount ?? 0)
          : (p.total ?? 0);
        if (p.method === 'cash')     bd.cash     += effective;
        if (p.method === 'card')     bd.card     += effective;
        if (p.method === 'transfer') bd.transfer += effective;
        if (p.method === 'mixed')    bd.mixed    += effective;
      }
      setBreakdown(bd);
    }).catch(() => {});
  }, [session?._id]);

  if (!session) {
    navigate('/open-session', { replace: true });
    return null;
  }

  const diff = closingCash
    ? Number(closingCash) - (session?.expected_cash ?? 0)
    : null;

  const handleClose = async () => {
    setError(null);
    setLoading(true);
    try {
      const payload = {};
      if (closingCash !== '') payload.closing_cash = Number(closingCash);
      if (notes)              payload.notes        = notes;
      const closed = await closeSession(session._id, payload);
      // closeSess() se llama en CorteScreen al montar — si lo llamamos aquí,
      // SessionRoute detecta session===null y redirige antes de que llegue a /corte
      navigate('/corte', { state: { session: closed, terminal }, replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al cerrar caja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Cierre de caja</h1>
        </div>

        {/* Resumen del turno */}
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span>Ventas realizadas</span>
            <strong>{session?.sales_count ?? 0}</strong>
          </div>
          <div className={styles.summaryRow}>
            <span>Total vendido</span>
            <strong>{fmt(session?.sales_total)}</strong>
          </div>
          <div className={styles.summaryRow}>
            <span>Efectivo esperado en caja</span>
            <strong>{fmt(session?.expected_cash)}</strong>
          </div>
        </div>

        {/* Desglose por método (en vivo) */}
        {breakdown && (breakdown.cash + breakdown.card + breakdown.transfer + breakdown.mixed) > 0 && (
          <div className={styles.breakdown}>
            <p className={styles.breakdownTitle}>Desglose por forma de pago</p>
            {breakdown.cash     > 0 && <div className={styles.breakdownRow}><span><IcoCash size={14} /> Efectivo</span><strong>{fmt(breakdown.cash)}</strong></div>}
            {breakdown.card     > 0 && <div className={styles.breakdownRow}><span><IcoCard size={14} /> Tarjeta</span><strong>{fmt(breakdown.card)}</strong></div>}
            {breakdown.transfer > 0 && <div className={styles.breakdownRow}><span><IcoTransfer size={14} /> Transferencia</span><strong>{fmt(breakdown.transfer)}</strong></div>}
            {breakdown.mixed    > 0 && <div className={styles.breakdownRow}><span><IcoMixed size={14} /> Mixto</span><strong>{fmt(breakdown.mixed)}</strong></div>}
          </div>
        )}

        {/* Conteo de efectivo */}
        <div className={styles.field}>
          <label className={styles.label}>Efectivo contado al cerrar</label>
          <div className={styles.currencyRow}>
            <span className={styles.currencySymbol}>$</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              placeholder={(session?.expected_cash ?? 0).toFixed(2)}
              value={closingCash}
              onChange={(e) => setClosingCash(e.target.value)}
              style={{ paddingLeft: 32, fontSize: 18 }}
            />
          </div>
          {diff !== null && (
            <div className={`${styles.diff} ${diff < 0 ? styles.diffNeg : diff > 0 ? styles.diffPos : styles.diffZero}`}>
              {diff === 0
                ? <><IcoCheck size={13} /> Sin diferencia</>

                : diff > 0
                ? `+${fmt(diff)} sobrante`
                : `${fmt(diff)} faltante`}
            </div>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Notas (opcional)</label>
          <textarea
            className="input"
            rows={3}
            placeholder="Observaciones del turno…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ minHeight: 80, paddingTop: 10, resize: 'none' }}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <button
          className="btn btn--danger btn--full"
          onClick={handleClose}
          disabled={loading}
        >
          {loading ? <span className="spinner" /> : 'Cerrar caja'}
        </button>
      </div>
    </div>
  );
}
